import type { Guest, RSVPStatus } from '../types/Guests';

/**
 * A guest's RSVP status as the API derives it from their per-event answers
 * (kalligo-api OCE-626): confirmed if attending any invited event, declined if
 * declined every invited event, pending otherwise, and the stored value when
 * the guest has no invited events. `Guest.rsvp_status` is the stored legacy
 * value and is only used as the fallback, never to count or display.
 */
type WithRsvp = Pick<Guest, 'rsvp_status'> & { rsvp_status_derived?: RSVPStatus | null };

/** The status every tracker, filter and count should use. */
export function effectiveRsvpStatus(guest: WithRsvp): RSVPStatus {
  return guest.rsvp_status_derived ?? guest.rsvp_status ?? 'pending';
}

/** True when the stored legacy status disagrees with the derived one. */
export function hasRsvpMismatch(guest: WithRsvp): boolean {
  return !!guest.rsvp_status_derived && guest.rsvp_status_derived !== guest.rsvp_status;
}

export interface RsvpCounts {
  confirmed: number;
  pending: number;
  declined: number;
  awaiting_plus_one: number;
  other: number;
}

/** One pass, each guest in exactly one bucket, so the buckets always sum to the total. */
export function countByRsvpStatus(guests: readonly WithRsvp[]): RsvpCounts {
  const counts: RsvpCounts = { confirmed: 0, pending: 0, declined: 0, awaiting_plus_one: 0, other: 0 };
  for (const guest of guests) {
    const status = effectiveRsvpStatus(guest);
    if (status === 'confirmed' || status === 'pending' || status === 'declined' || status === 'awaiting_plus_one') counts[status] += 1;
    else counts.other += 1;
  }
  return counts;
}
